/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable @typescript-eslint/no-explicit-any */
import { useToast } from "@/hooks/use-toast";
import { zodResolver } from "@hookform/resolvers/zod";
import { useEffect, useState } from "react";
import { useForm, useWatch } from "react-hook-form";
import { z } from "zod";

type UseUserformType = {
  formData: any;
  onFormSubmit: (prop: any) => Promise<any>;
};

const emptyMultiDocDetectionKeys = {
  daily_sales_report: [],
  bank_deposit_slip: [],
  pos_payout_receipt: [],
};

const emptyWorkflowSettings = {
  solution_id: "",
  username: "",
  password: "",
};

const formSchema = z.object({
  erp_enabled: z.boolean().optional(),
  erp_price_validations: z.boolean().optional(),
  erp_sku_validations: z.boolean().optional(),
  erp_uom_validations: z.boolean().optional(),
  openapi_key: z.string().optional(),
  default_workflow: z.string().optional(),
  default_uom: z.string().optional(),
  reference_number_prefix: z.string().optional(),
  premium_limit: z.number(),
  premium_used: z.number(),
  order_status: z.string().optional(),
  snc_username: z.string().optional(),
  snc_password: z.string(),
  amend_and_push_duplicate: z.boolean().optional(),
  do_not_push_duplicate: z.boolean().optional(),
  push_duplicate: z.boolean().optional(),
  sku_similarity_search: z.boolean().optional(),
  customer_sku_search: z.boolean().optional(),
  ignore_validation_failure: z.boolean().optional(),
  ignore_price_validations: z.any().optional(),
  support_multi_doc_type: z.boolean().optional(),
  detect_chinese_text: z.boolean().optional(),
  disable_snc_auto_push: z.boolean().optional(),
  push_flagged_documents: z.boolean().optional(),
  match_sale_history_uom_by_sku: z.boolean().optional(),
  show_deleted_documents: z.boolean().optional(),
  show_branch_id: z.boolean().optional(),
  comments: z.string().optional(),
  decimal_places: z.any().optional(),
  amount_decimal_places: z.any().optional(),
  multi_doc_detection_keys: z
    .object({
      daily_sales_report: z.any().optional(),
      bank_deposit_slip: z.any().optional(),
      pos_payout_receipt: z.any().optional(),
    })
    .optional(),
  workflow_settings: z
    .object({
      solution_id: z.string().optional(),
      username: z.string().optional(),
      password: z.string().optional(),
    })
    .optional(),
});

export const useReactForm = ({ formData, onFormSubmit }: UseUserformType) => {
  const [show, setShow] = useState(false);
  const { toast } = useToast();

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    mode: "onBlur",
    defaultValues: {
      erp_enabled: false,
      openapi_key: "",
      premium_limit: 0,
      premium_used: 0,
      erp_price_validations: false,
      erp_sku_validations: false,
      erp_uom_validations: false,
      default_workflow: "",
      default_uom: "",
      reference_number_prefix: "",
      order_status: "",
      snc_password: "",
      snc_username: "",
      amend_and_push_duplicate: false,
      do_not_push_duplicate: false,
      push_duplicate: false,
      sku_similarity_search: false,
      customer_sku_search: false,
      ignore_validation_failure: false,
      support_multi_doc_type: false,
      detect_chinese_text: false,
      disable_snc_auto_push: false,
      push_flagged_documents: false,
      multi_doc_detection_keys: emptyMultiDocDetectionKeys,
      decimal_places: null,
      amount_decimal_places: null,
      match_sale_history_uom_by_sku: false,
      show_deleted_documents: false,
      show_branch_id: false,
      comments: "",
      workflow_settings: emptyWorkflowSettings,
    },
  });

  useEffect(() => {
    if (!formData) return;

    form.reset({
      erp_enabled: formData?.erp_enabled ?? false,
      openapi_key: formData?.openapi_key ?? "",
      premium_limit: formData?.premium_limit ?? 0,
      premium_used: formData?.premium_used ?? 0,
      erp_price_validations: formData?.erp_price_validations ?? false,
      erp_sku_validations: formData?.erp_sku_validations ?? false,
      erp_uom_validations: formData?.erp_uom_validations ?? false,
      default_workflow: formData?.default_workflow ?? "",
      default_uom: formData?.default_uom ?? "",
      reference_number_prefix: formData?.reference_number_prefix ?? "",
      order_status: formData?.order_status ?? "",
      snc_password: "",
      snc_username: formData?.snc_username ?? "",
      amend_and_push_duplicate: formData?.amend_and_push_duplicate ?? false,
      do_not_push_duplicate: formData?.do_not_push_duplicate ?? false,
      push_duplicate: formData?.push_duplicate ?? false,
      sku_similarity_search: formData?.sku_similarity_search ?? false,
      customer_sku_search: formData?.customer_sku_search ?? false,
      ignore_validation_failure: formData?.ignore_validation_failure ?? false,
      support_multi_doc_type: formData?.support_multi_doc_type ?? false,
      detect_chinese_text: formData?.detect_chinese_text ?? false,
      disable_snc_auto_push: formData?.disable_snc_auto_push ?? false,
      decimal_places: formData?.decimal_places ?? null,
      amount_decimal_places: formData?.amount_decimal_places ?? null,
      multi_doc_detection_keys:
        formData?.multi_doc_detection_keys ?? emptyMultiDocDetectionKeys,
      ignore_price_validations: formData.ignore_price_validations,
      match_sale_history_uom_by_sku:
        formData?.match_sale_history_uom_by_sku ?? false,
      show_deleted_documents: formData?.show_deleted_documents ?? false,
      show_branch_id: formData?.show_branch_id ?? false,
      comments: formData?.comments ?? "",
      workflow_settings: {
        solution_id: formData?.workflow_settings?.solution_id ?? "",
        username: formData?.workflow_settings?.username ?? "",
        password: formData?.workflow_settings?.password ?? "",
      },
    });
  }, [formData, form]);

  const erpEnabled = useWatch({
    control: form.control,
    name: "erp_enabled",
  });

  const support_multi_doc_type = useWatch({
    control: form.control,
    name: "support_multi_doc_type",
  });

  useEffect(() => {
    if (!erpEnabled) {
      form.setValue("erp_price_validations", false);
      form.setValue("erp_sku_validations", false);
      form.setValue("erp_uom_validations", false);
    }
  }, [erpEnabled, form]);

  useEffect(() => {
    if (!support_multi_doc_type) {
      form.setValue("multi_doc_detection_keys", undefined);
      return;
    }

    const currentValue = form.getValues("multi_doc_detection_keys");

    if (!currentValue) {
      form.setValue("multi_doc_detection_keys", emptyMultiDocDetectionKeys);
    }
  }, [support_multi_doc_type, form]);

  async function onSubmit(values: z.infer<typeof formSchema>) {
    const { data, error, errorDetails }: any = await onFormSubmit(values);

    if (data) {
      toast({
        title: "Success",
        variant: "success",
        description: data.status.msg,
      });
    }

    console.log({ error });

    if (errorDetails) {
      for (const key in errorDetails) {
        form.setError(key as any, { message: errorDetails[key] });
      }
    }
  }

  return { form, onSubmit, show, setShow };
};
